import { MAIN_DOMAIN, BRAND, SOCIAL_HANDLES } from './domain.config'

export interface SchemaTemplate {
  id: string
  name: string
  type: string
  description: string
  category: 'global' | 'page' | 'content'
  configurable: boolean
  template: any
}

/**
 * Schema Templates - Có thể config từ admin
 */
export const SCHEMA_TEMPLATES: SchemaTemplate[] = [
  {
    id: 'website',
    name: 'Website Schema',
    type: 'WebSite',
    description: 'Khai báo trang tổng, hỗ trợ search box',
    category: 'global',
    configurable: true,
    template: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "{{brandName}}",
      "alternateName": "{{alternateName}}",
      "url": "{{domain}}",
      "description": "{{brandDescription}}",
      "inLanguage": "{{inLanguage}}",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "{{searchActionTarget}}",
        "query-input": "required name=search_term_string"
      },
      "publisher": {
        "@type": "Organization",
        "name": "{{brandName}}",
        "url": "{{domain}}",
        "logo": {
          "@type": "ImageObject",
          "url": "{{logoUrl}}",
          "width": 512,
          "height": 512
        }
      }
    },
  },
  {
    id: 'organization',
    name: 'Organization Schema',
    type: 'SportsOrganization',
    description: 'Tăng độ tin cậy thương hiệu',
    category: 'global',
    configurable: true,
    template: {
      "@context": "https://schema.org",
      "@type": "SportsOrganization",
      "name": "{{brandName}}",
      "alternateName": "{{alternateName}}",
      "url": "{{domain}}",
      "logo": {
        "@type": "ImageObject",
        "url": "{{logoUrl}}"
      },
      "description": "{{brandDescription}}",
      "foundingDate": "{{foundingDate}}",
      "areaServed": {
        "@type": "Country",
        "name": "{{areaServedCountry}}"
      },
      "sport": "{{sport}}",
      "sameAs": "{{sameAs}}",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "{{contactPhone}}",
        "email": "{{contactEmail}}",
        "contactType": "Customer Service"
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "{{streetAddress}}",
        "addressLocality": "{{city}}",
        "addressRegion": "{{region}}",
        "postalCode": "{{postalCode}}",
        "addressCountry": "VN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "{{latitude}}",
        "longitude": "{{longitude}}"
      },
      "founder": {
        "@type": "Person",
        "name": "{{founderName}}",
        "jobTitle": "{{founderJobTitle}}"
      },
      "employee": "{{employees}}",
      "knowsAbout": "{{knowsAbout}}"
    }
  },
  {
    id: 'localbusiness',
    name: 'Local Business Schema',
    type: 'LocalBusiness',
    description: 'Gắn địa điểm Việt Nam (nếu có)',
    category: 'global',
    configurable: true,
    template: {
      "@context": "https://schema.org",
      "@type": "{{businessType}}",
      "name": "{{brandName}}",
      "alternateName": "{{alternateName}}",
      "description": "{{brandDescription}}",
      "url": "{{domain}}",
      "logo": {
        "@type": "ImageObject",
        "url": "{{logoUrl}}",
        "width": "{{logoWidth}}",
        "height": "{{logoHeight}}"
      },
      "image": {
        "@type": "ImageObject",
        "url": "{{imageUrl}}",
        "width": "{{imageWidth}}",
        "height": "{{imageHeight}}"
      },
      "telephone": "{{phone}}",
      "email": "{{email}}",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "{{streetAddress}}",
        "addressLocality": "{{city}}",
        "addressRegion": "{{region}}",
        "postalCode": "{{postalCode}}",
        "addressCountry": "VN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "{{latitude}}",
        "longitude": "{{longitude}}"
      },
      "openingHours": "{{openingHours}}",
      "priceRange": "{{priceRange}}",
      "sameAs": "{{sameAs}}",
      "foundingDate": "{{foundingDate}}",
      "areaServed": {
        "@type": "Country",
        "name": "{{areaServedCountry}}"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": "{{latitude}}",
          "longitude": "{{longitude}}"
        },
        "geoRadius": "{{serviceRadius}}"
      },
      "paymentAccepted": "{{paymentMethods}}",
      "currenciesAccepted": "{{currencies}}",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "{{catalogName}}",
        "itemListElement": "{{services}}"
      }
    }
  },
  {
    id: 'faq-global',
    name: 'FAQ Global Schema',
    type: 'FAQPage',
    description: 'Câu hỏi thường gặp hiển thị trên toàn site',
    category: 'global',
    configurable: true,
    template: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": "{{faqQuestions}}"
    }
  },
  {
    id: 'breadcrumb',
    name: 'Breadcrumb Schema',
    type: 'BreadcrumbList',
    description: 'Hiển thị đường dẫn trên SERP',
    category: 'page',
    configurable: false, // Auto generated
    template: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Trang chủ",
          "item": "{{domain}}"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "{{pageTitle}}",
          "item": "{{pageUrl}}"
        }
      ]
    }
  },
  {
    id: 'liveblog',
    name: 'Live Blog Posting Schema',
    type: 'LiveBlogPosting',
    description: 'Kích hoạt tính năng trực tiếp',
    category: 'content',
    configurable: true,
    template: {
      "@context": "https://schema.org",
      "@type": "LiveBlogPosting",
      "headline": "{{title}}",
      "description": "{{description}}",
      "author": {
        "@type": "{{authorType}}",
        "name": "{{authorName}}"
      },
      "publisher": {
        "@type": "Organization",
        "name": "{{brandName}}",
        "logo": {
          "@type": "ImageObject",
          "url": "{{domain}}/images/logo.png"
        }
      },
      "datePublished": "{{publishedDate}}",
      "dateModified": "{{modifiedDate}}",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "{{pageUrl}}"
      },
      "coverageStartTime": "{{coverageStartTime}}",
      "coverageEndTime": "{{coverageEndTime}}",
      "liveBlogUpdate": "{{liveBlogUpdates}}",
      "image": {
        "@type": "ImageObject",
        "url": "{{imageUrl}}",
        "width": "{{imageWidth}}",
        "height": "{{imageHeight}}"
      },
      "isLiveBroadcast": "{{isLive}}",
      "audience": {
        "@type": "Audience",
        "audienceType": "{{audience}}"
      },
      "broadcastChannelId": "{{broadcastChannel}}",
      "about": {
        "@type": "Event",
        "name": "{{relatedEvent}}",
        "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode"
      },
      "video": {
        "@type": "VideoObject",
        "contentUrl": "{{videoUrl}}",
        "embedUrl": "{{videoUrl}}"
      },
      "interactionStatistic": {
        "@type": "InteractionCounter",
        "interactionType": "https://schema.org/CommentAction",
        "url": "{{chatUrl}}"
      },
      "keywords": "{{keywords}}",
      "genre": "{{eventType}}"
    }
  },
  {
    id: 'newsarticle',
    name: 'News Article Schema',
    type: 'NewsArticle',
    description: 'Tối ưu bài viết, giúp hiển thị nổi bật',
    category: 'content',
    configurable: true,
    template: {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      "headline": "{{title}}",
      "description": "{{description}}",
      "image": {
        "@type": "ImageObject",
        "url": "{{imageUrl}}",
        "width": "{{imageWidth}}",
        "height": "{{imageHeight}}",
        "caption": "{{imageCaption}}"
      },
      "author": {
        "@type": "{{authorType}}",
        "name": "{{authorName}}",
        "url": "{{authorUrl}}"
      },
      "publisher": {
        "@type": "Organization",
        "name": "{{brandName}}",
        "logo": {
          "@type": "ImageObject",
          "url": "{{domain}}/images/logo.png"
        }
      },
      "datePublished": "{{publishedDate}}",
      "dateModified": "{{modifiedDate}}",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "{{pageUrl}}"
      },
      "articleSection": "{{section}}",
      "articleBody": "{{description}}",
      "keywords": "{{tags}}",
      "wordCount": "{{wordCount}}",
      "timeRequired": "PT{{readingTimeMinutes}}M",
      "inLanguage": "{{language}}",
      "locationCreated": "{{locationCreated}}",
      "isAccessibleForFree": "{{isAccessibleForFree}}",
      "copyrightYear": "{{copyrightYear}}",
      "copyrightHolder": "{{copyrightHolder}}",
      "sourceOrganization": {
        "@type": "Organization",
        "name": "{{sourceOrganization}}",
        "url": "{{sourceUrl}}"
      },
      "relatedLink": "{{relatedArticles}}",
      "category": "{{category}}"
    }
  },
  {
    id: 'faq',
    name: 'FAQ Page Schema',
    type: 'FAQPage',
    description: 'Hiển thị câu hỏi thường gặp trên SERP',
    category: 'content',
    configurable: true,
    template: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": "{{faqQuestions}}"
    }
  },
  {
    id: 'sportsevent',
    name: 'Sports Event Schema',
    type: 'SportsEvent',
    description: 'Thông tin trận đấu thể thao',
    category: 'content',
    configurable: true,
    template: {
      "@context": "https://schema.org",
      "@type": "SportsEvent",
      "name": "{{eventName}}",
      "description": "{{eventDescription}}",
      "startDate": "{{startDate}}",
      "eventStatus": "{{eventStatus}}",
      "location": {
        "@type": "Place",
        "name": "{{venueName}}",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "{{venueCity}}",
          "addressCountry": "{{venueCountry}}"
        }
      },
      "url": "{{pageUrl}}",
      "homeTeam": {
        "@type": "SportsTeam",
        "name": "{{homeTeam}}"
      },
      "awayTeam": {
        "@type": "SportsTeam",
        "name": "{{awayTeam}}"
      },
      "competitor": [
        {
          "@type": "SportsTeam",
          "name": "{{homeTeam}}"
        },
        {
          "@type": "SportsTeam",
          "name": "{{awayTeam}}"
        }
      ],
      "sport": "{{sport}}",
      "superEvent": {
        "@type": "SportsEvent",
        "name": "{{league}}",
        "season": "{{season}}"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock"
      },
      "workFeatured": {
        "@type": "VideoObject",
        "name": "Live Stream",
        "url": "{{broadcastUrl}}"
      },
      "organizer": {
        "@type": "Organization",
        "name": "{{organizerName}}",
        "url": "{{domain}}"
      },
      "potentialAction": {
        "@type": "WatchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "{{pageUrl}}",
          "actionPlatform": [
            "http://schema.org/DesktopWebPlatform",
            "http://schema.org/MobileWebPlatform"
          ]
        }
      }
    }
  },
  {
    id: 'videoobject',
    name: 'Video Object Schema',
    type: 'VideoObject',
    description: 'Tối ưu video highlights',
    category: 'content',
    configurable: true,
    template: {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      "name": "{{videoTitle}}",
      "description": "{{videoDescription}}",
      "thumbnailUrl": "{{thumbnailUrl}}",
      "uploadDate": "{{uploadDate}}",
      "duration": "{{duration}}",
      "contentUrl": "{{videoUrl}}",
      "embedUrl": "{{embedUrl}}",
      "publisher": {
        "@type": "Organization",
        "name": "{{brandName}}",
        "logo": {
          "@type": "ImageObject",
          "url": "{{domain}}/images/logo.png"
        }
      },
      "interactionStatistic": [
        {
          "@type": "InteractionCounter",
          "interactionType": "https://schema.org/WatchAction",
          "userInteractionCount": "{{viewCount}}"
        },
        {
          "@type": "InteractionCounter",
          "interactionType": "https://schema.org/LikeAction",
          "userInteractionCount": "{{interactionCount}}"
        }
      ],
      "genre": "{{genre}}",
      "videoQuality": "{{videoQuality}}",
      "encodingFormat": "{{encodingFormat}}",
      "contentSize": "{{contentSize}}MB"
    }
  }
]

/**
 * Get schema template by ID
 */
export function getSchemaTemplate(id: string): SchemaTemplate | undefined {
  return SCHEMA_TEMPLATES.find(template => template.id === id)
}

/**
 * Get all schema templates by category
 */
export function getSchemasByCategory(category: string): SchemaTemplate[] {
  return SCHEMA_TEMPLATES.filter(template => template.category === category)
}

/**
 * Replace template variables with actual values
 */
export function processSchemaTemplate(template: any, variables: Record<string, any>): any {
  let schemaString = JSON.stringify(template)

  // Special handling for FAQ questions
  if (variables.faqQuestions && Array.isArray(variables.faqQuestions)) {
    schemaString = schemaString.replace('"{{faqQuestions}}"', JSON.stringify(variables.faqQuestions))
  }

  // Special handling for sameAs (social media links array)
  if (variables.sameAs) {
    const sameAsArray = Array.isArray(variables.sameAs)
      ? variables.sameAs.filter(url => url && url.trim() !== '')
      : [variables.sameAs].filter(url => url && url.trim() !== '')
    schemaString = schemaString.replace('"{{sameAs}}"', JSON.stringify(sameAsArray))
  }

  // Special handling for employees (JSON array)
  if (variables.employees) {
    try {
      const employeesArray = typeof variables.employees === 'string'
        ? JSON.parse(variables.employees)
        : variables.employees
      schemaString = schemaString.replace('"{{employees}}"', JSON.stringify(employeesArray))
    } catch (e) {
      // If JSON parsing fails, treat as empty array
      schemaString = schemaString.replace('"{{employees}}"', '[]')
    }
  }

  // Special handling for knowsAbout (convert comma-separated to array)
  if (variables.knowsAbout) {
    const knowsAboutArray = typeof variables.knowsAbout === 'string'
      ? variables.knowsAbout.split(',').map(item => item.trim()).filter(item => item.length > 0)
      : variables.knowsAbout
    schemaString = schemaString.replace('"{{knowsAbout}}"', JSON.stringify(knowsAboutArray))
  }

  // Special handling for mainEntityItems (JSON array)
  if (variables.mainEntityItems) {
    try {
      const mainEntityArray = typeof variables.mainEntityItems === 'string'
        ? JSON.parse(variables.mainEntityItems)
        : variables.mainEntityItems
      schemaString = schemaString.replace('"{{mainEntityItems}}"', JSON.stringify(mainEntityArray))
    } catch (e) {
      // If JSON parsing fails, remove the field
      schemaString = schemaString.replace('"mainEntity": "{{mainEntityItems}}",', '')
      schemaString = schemaString.replace(',"mainEntity": "{{mainEntityItems}}"', '')
    }
  }

  // Special handling for liveBlogUpdates (JSON array)
  if (variables.liveBlogUpdates) {
    try {
      const updatesArray = Array.isArray(variables.liveBlogUpdates)
        ? variables.liveBlogUpdates.filter(update => update && (update.content || update.time))
        : JSON.parse(variables.liveBlogUpdates)
      schemaString = schemaString.replace('"{{liveBlogUpdates}}"', JSON.stringify(updatesArray))
    } catch (e) {
      // If JSON parsing fails, treat as empty array
      schemaString = schemaString.replace('"{{liveBlogUpdates}}"', '[]')
    }
  }

  // Special handling for tags (convert array to comma-separated string for keywords)
  if (variables.tags) {
    const tagsArray = Array.isArray(variables.tags)
      ? variables.tags.filter(tag => tag && tag.trim() !== '')
      : [variables.tags].filter(tag => tag && tag.trim() !== '')
    const tagsString = tagsArray.join(', ')
    schemaString = schemaString.replace('"{{tags}}"', `"${tagsString}"`)
  }

  // Special handling for relatedArticles (JSON array)
  if (variables.relatedArticles) {
    try {
      const articlesArray = Array.isArray(variables.relatedArticles)
        ? variables.relatedArticles.filter(article => article && (article.url || article.title))
        : JSON.parse(variables.relatedArticles)
      schemaString = schemaString.replace('"{{relatedArticles}}"', JSON.stringify(articlesArray))
    } catch (e) {
      // If JSON parsing fails, treat as empty array
      schemaString = schemaString.replace('"{{relatedArticles}}"', '[]')
    }
  }

  // Special handling for keywords (convert comma-separated to string)
  if (variables.keywords) {
    const keywordsString = typeof variables.keywords === 'string'
      ? variables.keywords
      : Array.isArray(variables.keywords)
        ? variables.keywords.join(', ')
        : String(variables.keywords)
    schemaString = schemaString.replace('"{{keywords}}"', `"${keywordsString}"`)
  }

  // Special handling for boolean values
  const booleanFields = ['isLive', 'isAccessibleForFree']
  booleanFields.forEach(field => {
    if (variables[field] !== undefined) {
      const boolValue = variables[field] === true || variables[field] === 'true' || variables[field] === '1'
      schemaString = schemaString.replace(`"{{${field}}}"`, boolValue.toString())
    }
  })

  // Special handling for numeric values
  const numericFields = ['imageWidth', 'imageHeight', 'wordCount', 'readingTimeMinutes', 'venueCapacity', 'viewCount', 'interactionCount', 'copyrightYear']
  numericFields.forEach(field => {
    if (variables[field] !== undefined && variables[field] !== '') {
      const numValue = parseInt(variables[field]) || 0
      schemaString = schemaString.replace(`"{{${field}}}"`, numValue.toString())
    }
  })

  // Replace all other variables {{variableName}}
  Object.entries(variables).forEach(([key, value]) => {
    // Skip special fields already handled
    if (!['faqQuestions', 'sameAs', 'employees', 'knowsAbout', 'mainEntityItems', 'liveBlogUpdates', 'isLive', 'tags', 'relatedArticles', 'keywords'].includes(key) &&
      !numericFields.includes(key)) {
      const regex = new RegExp(`{{${key}}}`, 'g')
      schemaString = schemaString.replace(regex, String(value || ''))
    }
  })

  // Clean up any remaining template variables with empty strings
  schemaString = schemaString.replace(/{{[^}]+}}/g, '')

  // Remove fields with empty values
  let parsedSchema = JSON.parse(schemaString)
  parsedSchema = removeEmptyFields(parsedSchema)

  return parsedSchema
}

/**
 * Remove empty fields from schema object
 */
function removeEmptyFields(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(removeEmptyFields).filter(item => item !== null && item !== undefined && item !== '')
  }

  if (obj !== null && typeof obj === 'object') {
    const cleaned: any = {}
    Object.entries(obj).forEach(([key, value]) => {
      const cleanedValue = removeEmptyFields(value)
      if (cleanedValue !== null && cleanedValue !== undefined && cleanedValue !== '' &&
        !(Array.isArray(cleanedValue) && cleanedValue.length === 0) &&
        !(typeof cleanedValue === 'object' && Object.keys(cleanedValue).length === 0)) {
        cleaned[key] = cleanedValue
      }
    })
    return cleaned
  }

  return obj
}

/**
 * Get default variables for schema processing
 */
export function getDefaultSchemaVariables(): Record<string, any> {
  return {
    // Domain & Brand
    domain: MAIN_DOMAIN,
    brandName: BRAND.NAME,
    brandShortName: BRAND.SHORT_NAME,
    brandDescription: BRAND.DESCRIPTION,

    // Social Links
    facebookUrl: SOCIAL_HANDLES.FACEBOOK,
    twitterUrl: SOCIAL_HANDLES.TWITTER,
    youtubeUrl: SOCIAL_HANDLES.YOUTUBE,
    telegramUrl: SOCIAL_HANDLES.TELEGRAM,

    // Organization Schema defaults
    foundingDate: '2017',
    sport: 'Soccer',
    areaServedCountry: 'Vietnam',
    logoUrl: `${MAIN_DOMAIN}/images/logo.png`,
    sameAs: [
      SOCIAL_HANDLES.FACEBOOK,
      SOCIAL_HANDLES.TWITTER,
      SOCIAL_HANDLES.YOUTUBE,
      SOCIAL_HANDLES.TELEGRAM
    ],
    founderName: 'Đinh Hoàng Nam',
    founderJobTitle: 'CEO & Founder',

    // Website Schema defaults
    alternateName: BRAND.SHORT_NAME,
    inLanguage: 'vi-VN',
    searchActionTarget: `${MAIN_DOMAIN}/search?q={search_term_string}`,
    publisherType: 'SportsOrganization',

    // Contact Information
    contactPhone: '+8484983965879',
    contactEmail: 'contact@xoilactv.tech',
    phone: '+84983965879',
    email: 'contact@xoilactv.tech',

    // Location Information
    streetAddress: '123 Đường ABC',
    city: 'Hà Nội',
    region: 'Hà Nội',
    postalCode: '100000',
    latitude: '21.0285',
    longitude: '105.8542',

    // Local Business defaults
    openingHours: 'Mo-Su 00:00-23:59',
    priceRange: 'Miễn phí',
    businessType: 'LocalBusiness',

    // News Article defaults
    authorName: 'Xoilac',
    authorType: 'Organization',
    authorUrl: `${MAIN_DOMAIN}/author/xoilac`,
    category: 'Thể thao',
    section: 'Tin tức',
    imageWidth: '800',
    imageHeight: '400',
    imageCaption: 'Hình ảnh minh họa',
    language: 'vi',
    locationCreated: 'Hà Nội, Việt Nam',
    isAccessibleForFree: 'true',
    copyrightYear: new Date().getFullYear().toString(),
    copyrightHolder: BRAND.NAME,
    sourceOrganization: BRAND.NAME,

    // Live Blog defaults
    audience: 'Everyone',
    isLive: 'false',
    broadcastChannel: 'Xoilac',
    eventType: 'football',
    relatedEvent: 'Trận đấu bóng đá',

    // Sports Event defaults
    league: 'Ngoại Hạng Anh',
    season: '2024-2025',

    // Video Object defaults
    genre: 'Sports',
    videoQuality: 'HD',
    encodingFormat: 'mp4',
    duration: 'PT5M',

    // Current date for timestamps
    publishedDate: new Date().toISOString(),
    modifiedDate: new Date().toISOString(),
    uploadDate: new Date().toISOString(),

    // Sample JSON data for complex fields
    employees: JSON.stringify([
      {
        "@type": "Person",
        "name": "Võ Trung Thành",
        "jobTitle": "CEO & Founder"
      },
      {
        "@type": "Person",
        "name": "Giàng A Lử",
        "jobTitle": "Bình luận viên thể thao"
      },
      {
        "@type": "Person",
        "name": "Giàng A Lôi",
        "jobTitle": "Bình luận viên thể thao"
      },
      {
        "@type": "Person",
        "name": "Giàng A Phò",
        "jobTitle": "Bình luận viên thể thao"
      }
    ]),

    knowsAbout: 'CakhiaTV, Cakhia TV, Cà Khịa TV, Cakhia TV | Trực Tiếp Bóng Đá HD, trực tiếp bóng đá, Ngoại hạng Anh, Champions League, La Liga, V-League',

    mainEntityItems: JSON.stringify([
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Lịch thi đấu",
        "url": `${MAIN_DOMAIN}/lich-thi-dau-bong-da`
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Kết quả",
        "url": `${MAIN_DOMAIN}/kqbd`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Highlights",
        "url": `${MAIN_DOMAIN}/highlights`
      }
    ]),

    liveBlogUpdates: JSON.stringify([])
  }
} 